/** UX-GROWTH H4: "What's new" button (with an unread dot) and side panel. Content: data/whatsNew.ts. */
import { useMemo, useState } from 'react';
import { Sparkles } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { whatsNewFor, hasUnseen, markSeen } from '../data/whatsNew';

const fmt = (d: string) => new Date(`${d}T12:00:00Z`).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });

export default function WhatsNew({ role, appVersion }: { role?: string; appVersion?: string }) {
  const entries = useMemo(() => whatsNewFor(role), [role]);
  const [open, setOpen] = useState(false);
  const [unseen, setUnseen] = useState(() => hasUnseen(entries));
  if (!entries.length) { return null; }

  const onOpenChange = (next: boolean) => {
    setOpen(next);
    if (next) { markSeen(entries); setUnseen(false); }
  };

  return (
    <>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onOpenChange(true); }}
        className="relative inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-xs font-medium text-white/85 hover:bg-white/10 hover:text-white"
        aria-label={unseen ? "What's new (new updates)" : "What's new"}
        data-testid="whats-new-button"
      >
        <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
        <span className="max-sm:hidden">What&apos;s new</span>
        {unseen && <span className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-amber-400" data-testid="whats-new-dot" />}
      </button>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto">
          <SheetHeader>
            <SheetTitle>What&apos;s new</SheetTitle>
            <SheetDescription>Recent changes to the portal{appVersion ? ` (version ${appVersion})` : ''}.</SheetDescription>
          </SheetHeader>
          <ol className="m-0 list-none space-y-4 px-4 pb-6">
            {entries.map(e => (
              <li key={e.id} className="border-b border-border pb-4 last:border-0">
                <div className="text-xs text-muted-foreground">{fmt(e.date)}</div>
                <h3 className="m-0 mt-0.5 text-sm font-semibold text-content">{e.title}</h3>
                <p className="m-0 mt-1 text-sm text-content-secondary">{e.body}</p>
              </li>
            ))}
          </ol>
        </SheetContent>
      </Sheet>
    </>
  );
}
